import React, { useEffect, useState } from 'react';
import { apiGet, apiPost, apiPut, apiDel } from '../../lib/api';
import { toast } from 'sonner';
import { Plus, X, Pencil, Trash2, AlertTriangle, ArrowUp, ArrowDown } from 'lucide-react';

const blank = () => ({
  name: '', category: '', unit: 'Nos', current_stock: 0,
  reorder_level: 0, monthly_consumption: '', vendor_name: '', unit_cost: '', location: '', notes: '',
});

export default function AdminSupplies() {
  const [items, setItems]   = useState([]);
  const [lookup, setLookup] = useState({});
  const [showForm, setShowForm]   = useState(false);
  const [editing, setEditing]     = useState(null);
  const [form, setForm]           = useState(blank());
  const [saving, setSaving]       = useState(false);
  const [lowOnly, setLowOnly]     = useState(false);
  const [showStock, setShowStock] = useState(null); // { id, type: 'in'|'out' }
  const [stockQty, setStockQty]   = useState('');
  const [stockNote, setStockNote] = useState('');
  const [migrating, setMigrating] = useState(false);
  const [migrateReport, setMigrateReport] = useState(null);

  const load = () => {
    apiGet(`/admin/supplies${lowOnly ? '?low_stock=true' : ''}`).then(r => setItems(r.items || [])).catch(() => {});
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [lowOnly]);
  useEffect(() => { apiGet('/admin/lookup').then(setLookup).catch(() => {}); }, []);

  const openNew  = ()  => { setEditing(null); setForm(blank()); setShowForm(true); };
  const openEdit = (r) => {
    setEditing(r);
    setForm({ name: r.name, category: r.category, unit: r.unit, current_stock: r.current_stock,
      reorder_level: r.reorder_level, monthly_consumption: r.monthly_consumption || '',
      vendor_name: r.vendor_name || '', unit_cost: r.unit_cost || '', location: r.location || '', notes: r.notes || '' });
    setShowForm(true);
  };

  const save = async (e) => {
    e.preventDefault();
    if (!form.name || !form.category) { toast.error('Name and Category required'); return; }
    setSaving(true);
    // Convert empty string numeric fields to null for Pydantic validation
    const payload = {
      ...form,
      unit_cost: form.unit_cost === '' ? null : Number(form.unit_cost) || null,
      monthly_consumption: form.monthly_consumption === '' ? null : Number(form.monthly_consumption) || null,
    };
    try {
      if (editing) {
        await apiPut(`/admin/supplies/${editing.id}`, payload);
        toast.success('Item updated');
      } else {
        await apiPost('/admin/supplies', payload);
        toast.success('Item added');
      }
      setShowForm(false); load();
    } catch { toast.error('Could not save'); } finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!window.confirm('Remove this supply item?')) return;
    try { await apiDel(`/admin/supplies/${id}`); toast.success('Deleted'); load(); }
    catch { toast.error('Could not delete'); }
  };

  const adjustStock = async () => {
    if (!stockQty || isNaN(Number(stockQty))) { toast.error('Enter valid quantity'); return; }
    const qty = showStock.type === 'in' ? +stockQty : -Math.abs(+stockQty);
    try {
      await apiPut(`/admin/supplies/${showStock.id}/stock`, { qty_change: qty, reason: showStock.type === 'in' ? 'restock' : 'issued', remarks: stockNote });
      toast.success('Stock updated');
      setShowStock(null); setStockQty(''); setStockNote(''); load();
    } catch { toast.error('Could not update stock'); }
  };

  const cats = lookup.supply_categories || [];
  const units = lookup.supply_units || [];

  return (
    <div data-testid="admin-supplies" className="space-y-4">
      {/* B-Admin §1 — Migration banner */}
      <div className="flex items-start gap-3 p-4 bg-amber-50 border border-amber-200 rounded-sm" data-testid="supplies-migration-banner">
        <AlertTriangle size={16} className="text-amber-600 mt-0.5 flex-shrink-0" />
        <div className="flex-1 text-sm text-amber-800">
          <strong>This screen will be read-only.</strong> Office supplies have been moved to <strong>Stores → Opening Stock / Godown</strong> (Central Godown). New receipts go through <strong>Stores → GRN → Office location</strong>.
          {!migrateReport && (
            <button onClick={async () => {
              setMigrating(true);
              try {
                const r = await apiPost('/stores/admin-supplies/migrate-to-stores', {});
                setMigrateReport(r);
              } catch { toast.error('Migration failed'); } finally { setMigrating(false); }
            }} disabled={migrating} className="ml-3 px-2 py-0.5 text-xs bg-amber-600 text-white rounded hover:bg-amber-700 disabled:opacity-50" data-testid="supplies-migrate-btn">
              {migrating ? 'Migrating…' : 'Migrate to Stores'}
            </button>
          )}
          {migrateReport && (
            <span className="ml-3 text-xs text-emerald-700 font-semibold">Migrated {migrateReport.migrated} items to Central Godown.</span>
          )}
        </div>
      </div>
      <div className="flex items-end justify-between">
        <div>
          <div className="text-[10px] font-display font-bold uppercase tracking-[0.2em] text-[#3b4872] mb-1">Administration</div>
          <h1 className="font-display text-2xl font-extrabold">Office Supplies Inventory</h1>
          <p className="text-sm text-slate-500 mt-0.5">Track office consumables, stationery and pantry supplies.</p>
        </div>
        <div className="flex gap-2">
          <button onClick={() => setLowOnly(v => !v)}
            className={`px-3 py-1.5 text-xs font-display font-bold uppercase tracking-wider rounded-sm border flex items-center gap-1.5 ${lowOnly ? 'bg-[#3b4872] text-white border-[#3b4872]' : 'bg-white text-slate-600 border-slate-200'}`}>
            <AlertTriangle size={12} /> Low Stock
          </button>
          <button onClick={openNew} className="btn-primary flex items-center gap-2" data-testid="supply-add-btn">
            <Plus size={15} /> Add Item
          </button>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-sm overflow-hidden">
        <table className="w-full dense-table">
          <thead><tr>
            <th>Item</th><th>Category</th><th>Unit</th>
            <th className="text-right">Current Stock</th>
            <th className="text-right">Reorder Level</th>
            <th>Vendor</th><th>Location</th><th></th>
          </tr></thead>
          <tbody>
            {items.length === 0 && (
              <tr><td colSpan={8} className="text-center text-sm text-slate-400 py-8">No supply items found</td></tr>
            )}
            {items.map(r => {
              const isLow = r.current_stock <= r.reorder_level && r.reorder_level > 0;
              return (
                <tr key={r.id} data-testid={`supply-row-${r.id}`}>
                  <td className="font-display font-semibold">
                    {r.name}
                    {isLow && <span className="ml-2 text-[9px] font-bold text-amber-700 bg-amber-50 px-1 rounded">LOW</span>}
                    {r.current_stock === 0 && <span className="ml-2 text-[9px] font-bold text-red-700 bg-red-50 px-1 rounded">OUT</span>}
                  </td>
                  <td className="text-xs">{r.category}</td>
                  <td className="text-xs">{r.unit}</td>
                  <td className="text-right tabular-nums font-bold text-sm">{r.current_stock}</td>
                  <td className="text-right tabular-nums text-xs text-slate-500">{r.reorder_level}</td>
                  <td className="text-xs">{r.vendor_name || '—'}</td>
                  <td className="text-xs">{r.location || '—'}</td>
                  <td>
                    <div className="flex gap-1 justify-end">
                      <button onClick={() => { setShowStock({ id: r.id, type: 'in' }); setStockQty(''); }}
                        title="Stock In" className="text-emerald-500 hover:text-emerald-700" data-testid={`supply-in-${r.id}`}><ArrowUp size={13} /></button>
                      <button onClick={() => { setShowStock({ id: r.id, type: 'out' }); setStockQty(''); }}
                        title="Issue / Stock Out" className="text-amber-500 hover:text-amber-700" data-testid={`supply-out-${r.id}`}><ArrowDown size={13} /></button>
                      <button onClick={() => openEdit(r)} className="text-slate-400 hover:text-[#3b4872]" data-testid={`supply-edit-${r.id}`}><Pencil size={13} /></button>
                      <button onClick={() => remove(r.id)} className="text-red-400 hover:text-red-600" data-testid={`supply-del-${r.id}`}><Trash2 size={13} /></button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Stock adjust modal */}
      {showStock && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center" onClick={() => setShowStock(null)}>
          <div className="bg-white rounded-sm shadow-xl p-6 w-80 space-y-4" onClick={e => e.stopPropagation()}>
            <h3 className="font-display font-bold text-sm uppercase tracking-wider">
              {showStock.type === 'in' ? 'Stock In (Restock)' : 'Issue / Stock Out'}
            </h3>
            <div>
              <label className="label">Quantity</label>
              <input type="number" min="0" className="input" value={stockQty} onChange={e => setStockQty(e.target.value)} autoFocus data-testid="stock-qty" />
            </div>
            <div>
              <label className="label">Remarks (optional)</label>
              <input className="input" value={stockNote} onChange={e => setStockNote(e.target.value)} />
            </div>
            <div className="flex justify-end gap-2">
              <button onClick={() => setShowStock(null)} className="btn-ghost">Cancel</button>
              <button onClick={adjustStock} className="btn-primary" data-testid="stock-save">Confirm</button>
            </div>
          </div>
        </div>
      )}

      {/* Add/Edit Form Drawer */}
      {showForm && (
        <div className="fixed inset-0 bg-black/40 z-40 flex justify-end" onClick={() => setShowForm(false)}>
          <div className="w-full max-w-md bg-white h-full shadow-xl flex flex-col" onClick={e => e.stopPropagation()}>
            <header className="px-5 py-4 border-b flex items-center justify-between bg-[#3b4872] text-white">
              <h2 className="font-display font-bold text-sm uppercase tracking-wider">{editing ? 'Edit Item' : 'Add Supply Item'}</h2>
              <button onClick={() => setShowForm(false)}><X size={18} /></button>
            </header>
            <form onSubmit={save} className="flex-1 overflow-y-auto p-5 space-y-4">
              <div>
                <label className="label">Item Name *</label>
                <input className="input" value={form.name} onChange={e => setForm({...form, name: e.target.value})} data-testid="supply-name" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="label">Category *</label>
                  <select className="input" value={form.category} onChange={e => setForm({...form, category: e.target.value})}>
                    <option value="">Select</option>
                    {cats.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <div>
                  <label className="label">Unit</label>
                  <select className="input" value={form.unit} onChange={e => setForm({...form, unit: e.target.value})}>
                    {units.map(u => <option key={u} value={u}>{u}</option>)}
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="label">Current Stock</label>
                  <input type="number" min="0" className="input" value={form.current_stock} onChange={e => setForm({...form, current_stock: +e.target.value})} />
                </div>
                <div>
                  <label className="label">Reorder Level</label>
                  <input type="number" min="0" className="input" value={form.reorder_level} onChange={e => setForm({...form, reorder_level: +e.target.value})} />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="label">Unit Cost (₹)</label>
                  <input type="number" min="0" step="0.01" className="input" value={form.unit_cost} onChange={e => setForm({...form, unit_cost: e.target.value})} />
                </div>
                <div>
                  <label className="label">Monthly Usage</label>
                  <input type="number" min="0" className="input" value={form.monthly_consumption} onChange={e => setForm({...form, monthly_consumption: e.target.value})} />
                </div>
              </div>
              <div>
                <label className="label">Vendor</label>
                <input className="input" value={form.vendor_name} onChange={e => setForm({...form, vendor_name: e.target.value})} />
              </div>
              <div>
                <label className="label">Location in Office</label>
                <input className="input" value={form.location} onChange={e => setForm({...form, location: e.target.value})} placeholder="e.g. Admin cabin, storage room" />
              </div>
              <div>
                <label className="label">Notes</label>
                <textarea className="input h-16 resize-none" value={form.notes} onChange={e => setForm({...form, notes: e.target.value})} />
              </div>
            </form>
            <footer className="border-t px-5 py-3 flex justify-end gap-2 bg-slate-50">
              <button type="button" onClick={() => setShowForm(false)} className="btn-ghost">Cancel</button>
              <button onClick={save} disabled={saving} className="btn-primary" data-testid="supply-save">{saving ? 'Saving…' : (editing ? 'Update' : 'Add Item')}</button>
            </footer>
          </div>
        </div>
      )}
    </div>
  );
}
